
/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React from 'react';
import { motion } from 'framer-motion';

const NodePoint = ({ x, y, delay, status }: { x: string, y: string, delay: number, status: 'online' | 'degraded' }) => (
    <div className="absolute" style={{ left: x, top: y }}>
        <motion.div 
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay, duration: 0.5 }}
            className="relative"
        >
            <div className={`w-2 h-2 rounded-full ${status === 'online' ? 'bg-green-500' : 'bg-amber-500'}`}></div>
            <div className={`absolute -inset-1 rounded-full opacity-30 animate-ping ${status === 'online' ? 'bg-green-500' : 'bg-amber-500'}`}></div>
        </motion.div>
    </div>
);

export const NetworkMap: React.FC = () => {
    return (
        <div className="relative w-full h-64 bg-stone-900 rounded-xl overflow-hidden border border-stone-800">
            {/* Abstract Map Grid */}
            <div className="absolute inset-0 opacity-20" style={{ backgroundImage: 'radial-gradient(#C5A059 1px, transparent 1px)', backgroundSize: '20px 20px' }}></div>
            
            {/* World Map Silhouette (Simplified via CSS shapes or just abstract) */}
            <div className="absolute inset-0 flex items-center justify-center opacity-10">
                 <div className="text-[150px] font-serif text-white select-none">GHOST PROTOCOL</div>
            </div>

            {/* Nodes */}
            <NodePoint x="20%" y="30%" delay={0.1} status="online" /> {/* NA West */}
            <NodePoint x="25%" y="35%" delay={0.2} status="online" /> {/* NA East */}
            <NodePoint x="48%" y="25%" delay={0.3} status="online" /> {/* Europe */}
            <NodePoint x="52%" y="28%" delay={0.4} status="degraded" /> {/* Europe East */}
            <NodePoint x="75%" y="40%" delay={0.5} status="online" /> {/* Asia */}
            <NodePoint x="80%" y="60%" delay={0.6} status="online" /> {/* Aus */}
            <NodePoint x="30%" y="65%" delay={0.7} status="online" /> {/* SA */}

            {/* Connection Lines (SVG) */}
            <svg className="absolute inset-0 w-full h-full pointer-events-none opacity-30">
                <motion.path 
                    d="M 200 100 Q 400 50 600 120" 
                    fill="transparent" 
                    stroke="#C5A059" 
                    strokeWidth="1"
                    initial={{ pathLength: 0 }}
                    animate={{ pathLength: 1 }}
                    transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                />
                 <motion.path 
                    d="M 250 120 Q 300 200 700 180" 
                    fill="transparent" 
                    stroke="#C5A059" 
                    strokeWidth="1"
                    initial={{ pathLength: 0 }}
                    animate={{ pathLength: 1 }}
                    transition={{ duration: 3, repeat: Infinity, ease: "linear", delay: 1 }}
                />
            </svg>

            <div className="absolute bottom-4 left-4 flex gap-4 text-[10px] text-stone-400 font-mono uppercase">
                <div className="flex items-center gap-1"><div className="w-1.5 h-1.5 rounded-full bg-green-500"></div> Healthy Node</div>
                <div className="flex items-center gap-1"><div className="w-1.5 h-1.5 rounded-full bg-amber-500"></div> High Latency</div>
            </div>
        </div>
    );
};
